import React, { useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

export const UploadROV: React.FC = () => {
  const { documents, addDocument, navigateToDocument } = useApp();
  const [docName, setDocName] = useState('');
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocId, setUploadedDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  const uploadedDoc = uploadedDocId ? documents.find(d => d.id === uploadedDocId) : null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploading) return;

    const name = docName.trim() || selectedFile.name.replace(/\.[^.]+$/, '');
    const templateName = templateFile
      ? templateFile.name.replace(/\.[^.]+$/, '')
      : 'Auto-detect';

    setIsUploading(true);
    try {
      const docId = await addDocument(selectedFile, name, templateName, templateFile ?? undefined);
      setUploadedDocId(docId);
    } catch {
      // Error toast shown by context
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setDocName('');
      setTemplateFile(null);
    }
  };

  const handleReset = () => {
    setUploadedDocId(null);
    setSelectedFile(null);
    setDocName('');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Upload Section */}
      {!uploadedDocId && (
        <div className="glass-card p-8 animate-slide-up">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
              <Upload size={28} className="text-brand-400" />
            </div>
            <h2 className="text-xl font-semibold text-surface-900">Upload Document for Analysis</h2>
            <p className="text-sm text-surface-500 mt-1">
              Upload a document to run TRACE AI-powered analysis. Optionally provide a template as the knowledge base.
            </p>
          </div>

          {/* File Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer mb-6 ${isDragging ? 'upload-zone-active' : 'upload-zone'}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp,.gif"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center gap-4 p-2">
                <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center">
                  <FileText size={24} className="text-brand-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-surface-800">{selectedFile.name}</p>
                  <p className="text-xs text-surface-400 mt-0.5">{formatFileSize(selectedFile.size)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  className="text-surface-400 hover:text-surface-600 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className="text-center py-6">
                <Upload size={40} className="mx-auto mb-3 text-surface-300" />
                <p className="text-sm font-medium text-surface-600">Drop your document here</p>
                <p className="text-xs text-surface-400 mt-1">Supports PDF, DOC, DOCX, PNG, JPG, TIFF, BMP, WebP</p>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">
                Document Name
              </label>
              <input
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="e.g. Compliance Report v1.0 (auto-generated if blank)"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">
                Knowledge Base Template (Optional)
              </label>
              <p className="text-[10px] text-surface-400 mt-0.5 mb-1.5">
                Upload a template to use as the knowledge base for analysis. TRACE AI will compare your document against this template and report based solely on its content. Without a template, TRACE AI uses its own knowledge.
              </p>
              {templateFile ? (
                <div className="flex items-center gap-3 p-2.5 bg-surface-50 border border-surface-200 rounded-xl">
                  <FileText size={16} className="text-brand-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-surface-700 truncate flex-1">{templateFile.name}</span>
                  <button
                    onClick={() => setTemplateFile(null)}
                    className="text-surface-400 hover:text-surface-600 transition-colors flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => templateInputRef.current?.click()}
                  className="w-full p-2.5 border border-dashed border-surface-300 rounded-xl text-xs text-surface-400
                    hover:border-brand-400 hover:text-brand-400 transition-colors text-center"
                >
                  Click to upload knowledge base template
                </button>
              )}
              <input
                ref={templateInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp,.gif"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setTemplateFile(f); }}
                className="hidden"
              />
            </div>

            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="btn-primary w-full py-3 mt-2 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload Document
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Processing Status */}
      {uploadedDoc && (
        <div className="glass-card p-8 animate-slide-up">
          <div className="text-center mb-6">
            {uploadedDoc.status === 'processing' ? (
              <>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <Loader2 size={28} className="text-blue-500 animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-surface-900">Analyzing Document</h2>
                <p className="text-sm text-surface-500 mt-1">
                  TRACE AI is processing your document for compliance analysis...
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} className="text-emerald-500" />
                </div>
                <h2 className="text-xl font-semibold text-surface-900">Analysis Complete</h2>
                <p className="text-sm text-surface-500 mt-1">
                  Your document has been analyzed. {uploadedDoc.issuesDetected ?? 0} issue(s) detected.
                </p>
              </>
            )}
          </div>

          {/* Document Info */}
          <div className="bg-surface-50 rounded-2xl p-4 mb-6 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">File</span>
              <span className="font-medium text-surface-800">{uploadedDoc.fileName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">Document Name</span>
              <span className="font-mono text-brand-400">{uploadedDoc.docName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">Template</span>
              <span className="text-surface-800">{uploadedDoc.templateVersion}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-surface-500 mb-2">
              <span>Processing</span>
              <span>{uploadedDoc.processingProgress ?? 0}%</span>
            </div>
            <div className="h-2.5 bg-surface-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${uploadedDoc.status === 'completed'
                  ? 'bg-emerald-500'
                  : 'bg-brand-400'
                  }`}
                style={{ width: `${uploadedDoc.processingProgress ?? 0}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {uploadedDoc.status === 'completed' && (
              <button
                onClick={() => navigateToDocument(uploadedDoc.id)}
                className="btn-primary flex-1 flex items-center justify-center gap-2 py-3"
              >
                Check Analysis <ArrowRight size={16} />
              </button>
            )}
            <button
              onClick={handleReset}
              className="btn-secondary flex-1 py-3"
            >
              Upload Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
